import {
	ArrowBackOutlined,
	Close,
	ContentCopy,
	Download,
	Sort,
	UploadFile,
} from "@mui/icons-material";
import {
	Backdrop,
	Box,
	Button,
	CircularProgress,
	IconButton,
	Link,
	Menu,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import {
	ChangeEvent,
	FC,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
	addSubfolder,
	copyFile,
	copyFolder,
	deleteFileFromFolder,
	deleteSubfolder,
	ESRThunkDispatch,
	loadCurrentFolder,
	uploadFileToFolder,
} from "../../../actions/form";
import getBackendURL from "../../../api/api";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { download } from "../../../request";
import { ESRState } from "../../../types/data";
import { DrawerContainer } from "../DrawerContainer";
import { FileItem } from "./FileItem";
import { FolderItem } from "./FolderItem";
import { ClowderFile, ClowderFolder } from "./types";
import { getLabelForMimeType } from "./utils";

type Props = {
	open: boolean;
	onClose: () => void;
};

export const AttachmentList: FC<Props> = ({ open, onClose }) => {
	const { esrId = "", activeLabel="",activeId="" } = useParams();
	const [user, setUser] = useCurrentUser();
	const dispatch = useDispatch<ESRThunkDispatch>();

	const currentFolder = useSelector((state: ESRState) => state.current_folder);
	const { loading, path, folder_id: folderId } = currentFolder;
	const clipboard = useSelector((state: ESRState) => state.clipboard);

	useEffect(() => {
		if (activeId) {
			dispatch(loadCurrentFolder(activeId, ""));
		}
	}, [activeId]);

	useEffect(() => {
		if (activeId) {
			dispatch(loadCurrentFolder(activeId, folderId));
		}
	}, [open]);

	const [newFolderName, setNewFolderName] = useState("");

	const changeFolder = (toFolderId: string) => {
		dispatch(loadCurrentFolder(activeId, toFolderId));
	};

	const uploadFiles = async (e: ChangeEvent<HTMLInputElement>) => {
		if (!user) return;
		if (!e.target.files) return;
		for (const file of e.target.files) {
			dispatch(uploadFileToFolder(esrId, folderId, file, user.email, activeLabel));
		}
		e.target.value = ""; // clear file list
	};

	const addFolder = () => {
		if (!user) return;
		dispatch(addSubfolder(esrId, folderId, newFolderName));
		setNewFolderName("");
	};

	const deleteFolder = (subfolderId: string) => {
		if (!user) return;
		dispatch(deleteSubfolder(esrId, folderId, subfolderId));
	};

	const deleteFile = (fileId: string) => {
		if (!user) return;
		dispatch(deleteFileFromFolder(esrId, folderId, fileId));
	};

	const parentFolder = path.length > 1 ? path[path.length - 2] : null;
	const goToParentFolder = useCallback(() => {
		if (parentFolder) {
			changeFolder(parentFolder.folder_id);
		}
	}, [path]);

	const [sortBy, setSortBy] = useState("type");
	const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null);

	type Item = ClowderFolder | ClowderFile;

	const compareFn: (a: Item, b: Item) => number = useMemo(() => {
		const key = sortBy.startsWith("-") ? sortBy.slice(1) : sortBy;
		const order = sortBy.startsWith("-") ? "desc" : "asc";

		const lessThan = (a: Item, b: Item): boolean => {
			const aType =
				a.object_type === "file"
					? getLabelForMimeType(a.content_type.content_type).toLowerCase()
					: "";
			const bType =
				b.object_type === "file"
					? getLabelForMimeType(b.content_type.content_type).toLowerCase()
					: "";
			const aName = a.name.toLowerCase();
			const bName = b.name.toLowerCase();
			const aCreated = new Date(a.created);
			const bCreated = new Date(b.created);

			switch (key) {
				case "name":
					return aName === bName ? aType < bType : aName < bName;
				case "type":
					return aType === bType ? aName < bName : aType < bType;
				default:
					return aCreated === bCreated ? aName < bName : aCreated < bCreated;
			}
		};

		return (a, b) =>
			(order === "asc" && lessThan(a, b)) ||
			(order === "desc" && lessThan(b, a))
				? -1
				: 1;
	}, [sortBy]);

	return (
		<DrawerContainer open={open} onClose={onClose}>
			<Box
				sx={{
					width: 500,
					height: "100%",
					background: "white",
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						height: 60,
						display: "flex",
						flexDirection: "row",
						alignItems: "center",
						px: "24px",
						justifyContent: "space-between",
						background: "rgba(0, 62, 98, 0.02)",
					}}
				>
					{parentFolder && (
						<Button
							variant="text"
							startIcon={<ArrowBackOutlined />}
							onClick={goToParentFolder}
						>
							back to {parentFolder.folder_name}
						</Button>
					)}
					<Typography fontWeight={500} fontSize={14}>
						Memos
					</Typography>
					<Button
						startIcon={<Download />}
						onClick={() => {
							download(getBackendURL(`/esr/attachments?esr_id=${esrId}`));
						}}
					>
						Download
					</Button>
					<IconButton aria-label="close files" onClick={onClose}>
						<Close />
					</IconButton>
				</Box>

				<Stack direction="row" sx={{ m: "8px", flexWrap: "wrap" }}>
					<Link
						href="#"
						onClick={() => {
							changeFolder("");
						}}
					>
						Project Files
					</Link>
					/
					{path.map((p) => (
						<Box key={p.folder_id}>
							<Link
								sx={{ px: "2px" }}
								href="#"
								onClick={() => {
									changeFolder(p.folder_id);
								}}
							>
								{p.folder_name}
							</Link>
							/
						</Box>
					))}
				</Stack>

				<Stack
					direction="row"
					sx={{ px: 2, mb: 2, alignItems: "center" }}
				>
					{clipboard.length > 0 ? (
						<Stack direction="row" spacing={1}>
							<Button
								variant="contained"
								color="primary"
								sx={{ textTransform: "none" }}
								startIcon={<ContentCopy />}
								onClick={() => {
									for (const item of clipboard) {
										if (item.type === "file") {
											dispatch(
												copyFile(esrId, item.id, currentFolder.folder_id),
											);
										} else {
											dispatch(
												copyFolder(esrId, item.id, currentFolder.folder_id),
											);
										}
									}
								}}
							>
								Copy ({clipboard.length})
							</Button>
							<Button
								variant="outlined"
								sx={{ textTransform: "none" }}
								startIcon={<Download />}
								onClick={async () => {
									const params = new URLSearchParams({ esr_id: activeId });
									for (const item of clipboard) {
										params.append(item.type === "file" ? "file_id" : "folder_id", item.id);
									}
									await download(getBackendURL(`/esr/selected_attachments/?${params}`));
									dispatch({ type: "UPDATE_CLIPBOARD", update: [] });
								}}
							>
								Download ({clipboard.length})
							</Button>
							<Button
								variant="text"
								onClick={() => {
									dispatch({
										type: "UPDATE_CLIPBOARD",
										update: [],
									});
								}}
							>
								Cancel
							</Button>
						</Stack>
					) : (
						<Typography sx={{ color: "gray" }}>(No item selected)</Typography>
					)}
					<Box flex={1} />
					<Button
						size="small"
						startIcon={<Sort />}
						sx={{ textTransform: "none" }}
						onClick={(e) => setSortMenuAnchor(e.currentTarget)}
					>
						Sort by
					</Button>
					<Menu
						anchorEl={sortMenuAnchor}
						open={sortMenuAnchor !== null}
						onClose={() => setSortMenuAnchor(null)}
					>
						{[
							{ value: "name", label: "File name (A-Z)" },
							{ value: "-name", label: "File name (Z-A)" },
							{ value: "type", label: "File type (A - Z)" },
							{ value: "-type", label: "File type (Z - A)" },
							{ value: "created", label: "Date created (Oldest first)" },
							{ value: "-created", label: "Date created (Newest first)" },
						].map((option) => (
							<MenuItem
								key={option.value}
								selected={sortBy === option.value}
								onClick={() => {
									setSortBy(option.value);
									setSortMenuAnchor(null);
								}}
							>
								{option.label}
							</MenuItem>
						))}
					</Menu>
				</Stack>

				<Box sx={{ flex: 1, overflow: "scroll" }}>
					{currentFolder.content.toSorted(compareFn).map((node) =>
						node.object_type === "folder" ? (
							<FolderItem
								disabled={loading}
								key={node.id}
								folder={node}
								onClick={() => {
									changeFolder(node.id);
								}}
								onDelete={() => deleteFolder(node.id)}
							/>
						) : (
							<FileItem
								disabled={loading}
								key={node.id}
								file={node}
								onDelete={() => deleteFile(node.id)}
							/>
						),
					)}
					<Box sx={{ p: 5 }}>
						{!currentFolder.read_only && (
							<Stack direction="row" alignItems="center">
								<TextField
									label="Folder Name"
									value={newFolderName}
									onChange={(e) => setNewFolderName(e.target.value)}
								/>
								<Button onClick={addFolder}>Create Folder</Button>
							</Stack>
						)}
						{!currentFolder.read_only && (
							<Box sx={{ mt: "20px" }}>
								<Button
									component="label"
									variant="contained"
									startIcon={
										loading ? (
											<CircularProgress size={20} sx={{ color: "white" }} />
										) : (
											<UploadFile />
										)
									}
								>
									Upload files
									<input
										type="file"
										multiple
										style={{
											clipPath: "inset(50%)",
											height: 1,
											width: 1,
										}}
										onChange={uploadFiles}
									/>
								</Button>
							</Box>
						)}
					</Box>
				</Box>
			</Box>
			<Backdrop open={loading} sx={{ zIndex: 2, bgcolor: "rgba(0,0,0,0.1)" }}>
				<CircularProgress color="primary" />
			</Backdrop>
		</DrawerContainer>
	);
};
